import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setFormatLocale } from '@marcianus/shared/format';
import { setDsResolver } from '@marcianus/design/components';
import { DEFAULT_LOCALE, FORMAT_TAG, type Locale, REGION_TAG } from './config';
import { resolveLocale, storeLocale } from './detect';

// pt é o catálogo-fonte e o fallback final → vai no bundle (eager). Os outros
// idiomas carregam sob demanda, um chunk por ficheiro, e só o idioma ativo.
const ptEager = import.meta.glob<Record<string, unknown>>('./locales/pt/*.json', { eager: true, import: 'default' });
const lazy = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', { import: 'default' });

const nsOf = (path: string) => path.split('/').pop()!.replace('.json', '');

async function loadLanguage(lng: Locale) {
  const prefix = `./locales/${lng}/`;
  await Promise.all(Object.entries(lazy).filter(([p]) => p.startsWith(prefix)).map(async ([p, load]) => {
    const ns = nsOf(p);
    if (!i18next.hasResourceBundle(lng, ns)) i18next.addResourceBundle(lng, ns, await load(), true, true);
  }));
}

function applyDocumentLang(lng: Locale) {
  document.documentElement.lang = REGION_TAG[lng];
  // Formatadores partilhados (packages/shared/format) seguem o idioma ativo.
  setFormatLocale(FORMAT_TAG[lng], (k, o) => String(i18next.t(k as never, o as never)));
  // Texto do design system: 'ds.*' → ns design; 'taxo.*' → common; senão fica o fallback pt.
  setDsResolver((key, fallback, vars) =>
    String(i18next.t(key as never, { ns: key.startsWith('ds.') ? 'design' : 'common', defaultValue: fallback, ...vars } as never)));
}

export async function initI18n(profileLocale?: unknown) {
  const lng = resolveLocale({ profile: profileLocale });
  await i18next.use(initReactI18next).init({
    lng,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    resources: { pt: Object.fromEntries(Object.entries(ptEager).map(([p, r]) => [nsOf(p), r])) },
    interpolation: { escapeValue: false },   // React já escapa
    returnNull: false,
    // Chave em falta: visível em DEV (apanha-se cedo); em produção cai para pt.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (l, ns, key) => { if (import.meta.env.DEV) console.warn(`[i18n] chave em falta ${l}/${ns}:${key}`); },
  });
  // O Intl trata 'pt' como pt-BR (0 = singular → «0 participante»). O produto é pt-PT/AO.
  const pr = i18next.services.pluralResolver;
  const getRule = pr.getRule.bind(pr);
  pr.getRule = (code: string, o?: object) => getRule(code === 'pt' ? 'pt-PT' : code, o);
  await loadLanguage(lng);
  applyDocumentLang(lng);
  return lng;
}

/** Troca de idioma em runtime: sem reload, sem perder estado de formulários. */
export async function setLocale(lng: Locale, opts: { persist?: boolean } = {}) {
  if (lng === i18next.language) return;
  await loadLanguage(lng);
  await i18next.changeLanguage(lng);
  applyDocumentLang(lng);
  if (opts.persist !== false) storeLocale(lng);
}

export { i18next };
